/**
 * PerfectScenarioPage.tsx — A single applied scenario synthesising every
 *                           proven best-practice from documented aeration
 *                           interventions, calibrated for a Barcombe-class
 *                           shallow eutrophic reservoir (3-6 m mean depth,
 *                           ~3-10 ha, organic-rich cohesive bed, summer
 *                           cyano-T/O risk, polymictic).
 *
 * The page is prescriptive: each phase carries the design choice, the
 * numeric target, the evidence base, the module that predicts it, and the
 * KPI used to verify it after commissioning.  A single "Activate" button
 * enables the recommended train, loads the Barcombe defaults and drops the
 * user on the integrated Train Summary page with bathymetry and module
 * placement ready to refine.
 */

import { useNavigate } from 'react-router';
import {
  Sparkles, ArrowLeft, ClipboardCheck, Workflow, Wrench, Activity,
  Beaker, Layers, Wind, ShieldCheck, Target, Zap, Calculator,
  CheckCircle2, ChevronRight, BookOpen, AlertTriangle, Gauge,
  PoundSterling,
} from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { useModulesEnabled } from '@/lib/moduleRegistry';
import { getTrain } from '@/lib/moduleTrains';
import ModuleInfoModal from '@/components/ModuleInfoModal';
import { TRAIN_SCENARIO_KEY } from '@/lib/benchmarkSites';
import ReservoirHealthScorecard from '@/components/ReservoirHealthScorecard';
import { BARCOMBE_BATHY } from '@/lib/barcombeBathymetry';

const BARCOMBE_INPUTS = {
  area_m2: BARCOMBE_BATHY.area_m2,
  depth_mean_m: BARCOMBE_BATHY.meanDepth_m,
  depth_max_m: BARCOMBE_BATHY.maxDepth_m,
  chl_ugL: 30,
  chl_peak_ugL: 70,
  nom_mgL: 8,
  tn_mgL: 1.2,
  tp_mgL: 0.08,
  pH: 7.6,
  surfaceTempC: 19,
  hypoTempC: 11,
  stratified: true,
  fCyano: 0.45,
  windMs: 2.5,
  doSurface_mgL: 8.5,
  doHypo_mgL: 1.5,
  orp_mV: 50,
  N_brunt: 0.012,
  feII_mgL: 0.6,
  mnII_mgL: 0.45,
  diffuserQair_Ls: 2.0,
  diffuserDepth_m: Math.max(2.5, BARCOMBE_BATHY.maxDepth_m - 0.5),
  bubble_d_mm: 2.5,
};

const PERFECT_TRAIN_ID = 'shallow_uk_eutrophic';

interface Phase {
  num: number;
  icon: typeof Workflow;
  title: string;
  goal: string;
  steps: PhaseStep[];
}

interface PhaseStep {
  step: string;
  target: string;
  evidence: string;
  module?: string;
  kpi?: string;
  pitfall?: string;
}

const PHASES: Phase[] = [
  {
    num: 1,
    icon: ClipboardCheck,
    title: 'Pre-design — site characterisation',
    goal: 'Establish the physical baseline before specifying any hardware.',
    steps: [
      { step: 'Single-beam echo-sounder bathymetric survey at 25-m grid',
        target: 'Map ≥ 95 % of basin to 0.1 m vertical accuracy',
        evidence: 'Used at Loch Leven, Müggelsee, Bautzen (Driescher 1993)' },
      { step: 'Sediment cores: 4 stations × 30 cm depth, LOI + Fe:P + porewater P',
        target: 'Confirm OM < 25 %, Fe:P > 15, identify hottest internal-loading zone',
        evidence: 'Spears et al. 2007 (Loch Leven); Hupfer & Lewandowski 2008 (Müggelsee)',
        module: 'boudreau_diagenesis',
        kpi: 'Fe:P molar ratio > 15 → P stays bound under aeration' },
      { step: 'Weekly profiles April-October: T, DO, chl-a, Secchi, pH, ORP',
        target: 'Two full annual cycles minimum; capture stratification onset & breakdown',
        evidence: 'UKCEH LakeNet protocol; replicated at all benchmark sites',
        module: 'davison_fe_mn',
        kpi: 'Document baseline hypolimnion DO trajectory' },
      { step: '10-m wind & 1-m water-temperature data-logger on raft',
        target: '15-min interval, full season',
        evidence: 'Required for Wedderburn / MacIntyre / Wuest predictions',
        module: 'macintyre_convection',
        pitfall: 'Met-Office land-station wind under-estimates over-water values by 30-50 %' },
      { step: 'WTW raw-water log of Mn, Fe, geosmin, 2-MIB, PAC dose',
        target: '3 yrs back-data to quantify the OPEX baseline',
        evidence: 'Madgwick 2006 (Rutland); Wang 2010 (China T/O reservoirs)',
        module: 'geosmin_predictor',
        kpi: 'Establish "PAC-required weeks per year" baseline' },
    ],
  },
  {
    num: 2,
    icon: Workflow,
    title: 'Module-train selection',
    goal: 'Pick the conflict-free physics bundle that matches the reservoir profile.',
    steps: [
      { step: 'Use Train A — Shallow Eutrophic UK Reservoir',
        target: '12 modules covering diffuser → plume → bed → O₂ → algae → compliance',
        evidence: 'Validated against Loch Leven, Müggelsee, Bautzen',
        module: 'asaeda_c' },
      { step: 'Plume regime: Asaeda-Imberger C-criterion (shallow water)',
        target: 'C = 1.6-2.4 — Type-1 or transitional regime',
        evidence: 'Bautzen, Rutland, Hanningfield all operate in this window',
        module: 'asaeda_c',
        kpi: 'C < 1.4 → re-space modules' },
      { step: 'Bed physics: Mehta-Partheniades (replaces Bryant resuspension penalty)',
        target: 'τ_b at module proximity < 0.6 × τ_ce (typically < 0.08 Pa)',
        evidence: 'Amisk bed-scour 1988-91 forced raising diffusers 0.5 m',
        module: 'mehta_partheniades',
        pitfall: 'Do not stack with resusp_penalty — double-counts the same physics' },
      { step: 'Algae train: Kumagai feedback + N+P co-limitation + CSR groups',
        target: 'Captures cyano-stratification feedback loop and functional-group succession',
        evidence: 'Kumagai 2000 (Lake Biwa); Reynolds 2006 CSR framework',
        module: 'kumagai_feedback' },
      { step: 'Compliance: Geosmin/2-MIB predictor',
        target: '< 10 ng/L year-round',
        evidence: 'Rutland Water: 30 ng/L peaks reduced to < 8 ng/L after fine-bubble retrofit',
        module: 'geosmin_predictor',
        kpi: 'Geosmin model RMSE < 30 % vs WTW measurements after Year 1' },
    ],
  },
  {
    num: 3,
    icon: Wrench,
    title: 'Hardware specification',
    goal: 'Choose the diffuser type & sizing that the evidence base supports.',
    steps: [
      { step: 'Diffuser: fine-bubble membrane disc (EPDM, 1.5 µm pore)',
        target: 'Bubble release d₀ = 2.5 mm',
        evidence: 'Rutland 2000 retrofit; Bautzen 1998; replaces Helixor-era coarse-bubble',
        module: 'mcginnis_bubble',
        kpi: 'Observed OTE > 25 % during commissioning' },
      { step: 'Air flow per diffuser: 2.0 L/s (7.2 Nm³/h)',
        target: 'Within the 0.5-3 L/s sweet-spot for fine-bubble OTE',
        evidence: 'Stenstrom 2008; matches Bautzen and Rutland operating points',
        module: 'cost_per_o2' },
      { step: 'Module count: hex-grid covering deepest 30 % × ~1.8 plume diameters spacing',
        target: '~ 14-18 diffusers for Barcombe footprint (34 000 m²)',
        evidence: 'Asaeda-Imberger spacing rule applied at all benchmark sites',
        module: 'deep_zone' },
      { step: 'Blower: centralised on-shore VFD compressor, ducted to manifold',
        target: 'Match peak demand × 1.4 redundancy; SCR-controlled flow',
        evidence: 'Bautzen, Rutland, Hanningfield all use centralised manifold',
        pitfall: 'Avoid distributed sub-surface blowers — maintenance nightmare' },
      { step: 'Air-line: weighted HDPE 32 mm OD, hex-pattern lake-bed routing',
        target: 'Pressure-drop budget < 0.15 bar across manifold',
        evidence: 'Manufacturer standard (EDI, Aquaconsoft, Mixing Systems Inc.)' },
      { step: 'Whole-life cost target: < £1.5 / kg O₂ delivered',
        target: '20-year NPV (3.5 % HMT Green Book discount)',
        evidence: 'Stenstrom 2008; Ofwat PR19 benchmark for AMP-8 schemes',
        module: 'cost_per_o2',
        kpi: '£/kg O₂ < 1.5 in Year-1 OPEX report' },
    ],
  },
  {
    num: 4,
    icon: Layers,
    title: 'Layout & placement',
    goal: 'Position every module so plumes cover the basin without scour or dead-zones.',
    steps: [
      { step: 'Hexagonal staggered grid biased to deepest 30 % of footprint',
        target: 'Spacing = 1.8 × predicted plume diameter (~ 30-40 m for Barcombe)',
        evidence: 'Rutland 2000 retrofit hex-grid; Bautzen 2002 cross-line correction',
        module: 'deep_zone',
        kpi: 'Plume coverage map shows > 80 % of footprint' },
      { step: 'Diffuser elevation: 0.5 m above sediment-water interface',
        target: 'Avoid bed scour while staying within near-bed return-flow zone',
        evidence: 'Amisk 1991 lesson; Bautzen standard',
        module: 'mehta_partheniades',
        pitfall: 'Too close → scour uplifts SOD; too high → loses depth-resolved OTE' },
      { step: 'Verify Wedderburn number under design wind (3 m/s)',
        target: 'W > 1 with aeration on (mixed regime dominates)',
        evidence: 'Patterson & Imberger 1989; Spigel & Imberger 1980',
        module: 'wind_tilt',
        kpi: 'Wind tilt < 1 m at module centroids' },
      { step: 'Cluster modules upwind of intended coverage in prevailing-wind direction',
        target: 'Accept ~ 5-10 % asymmetric coverage gain for the windward bank',
        evidence: 'Loch Leven prevailing-SW wind documented (Maberly 2011)',
        module: 'wind_tilt' },
      { step: 'Internal-seiche resonance check',
        target: 'Diffuser duty cycle off any V1H1 / V2H1 harmonic by ≥ 25 %',
        evidence: 'Münnich, Wüest & Imboden 1992 (Lake Alpnach)',
        module: 'internal_seiche',
        kpi: 'No resonance flag at L7b validation step' },
    ],
  },
  {
    num: 5,
    icon: Activity,
    title: 'Commissioning & validation',
    goal: 'Prove the scheme meets every modelled prediction before handover.',
    steps: [
      { step: 'Cold-water OTE test: bubble-column profile at 4 representative diffusers',
        target: 'Measured OTE within ±15 % of McGinnis prediction',
        evidence: 'Standard ASCE 2-06 (2007) clean-water test protocol',
        module: 'mcginnis_bubble',
        kpi: '> 25 % OTE confirmed' },
      { step: '24-hr DO profile sweep before and after aeration switch-on',
        target: 'ΔDO at sediment interface > 3 mg/L within 7 days',
        evidence: 'Bautzen documented breakdown in 6 d (Hülsmann 2005)',
        module: 'davison_fe_mn' },
      { step: 'Thermistor chain logging (15-min) for first 4 weeks',
        target: 'Confirm stratification breakdown ≤ 14 days at design power',
        evidence: 'Bautzen breakdown in 6 d at 2400 Nm³/h',
        kpi: 'Continuous T-profile homogenisation within target' },
      { step: 'Sediment-trap deployment x 8 weeks, eroded vs deposited mass',
        target: 'No net erosion at any module — confirm Mehta-Partheniades stable regime',
        evidence: 'Mehta 1986 sediment-trap protocol',
        module: 'mehta_partheniades' },
      { step: 'Composite biological survey (chironomids + tubificids) at 4-month mark',
        target: 'Macrofauna density > 500 ind/m² recovering',
        evidence: 'Aller 1980/82; Müller et al. 2003 (Hallwil Tanytarsus return)',
        module: 'aller_bioturbation',
        kpi: 'Bioturbation index doubles vs baseline' },
    ],
  },
  {
    num: 6,
    icon: ShieldCheck,
    title: 'Operations & long-term management',
    goal: 'Sustain the gains, catch drift early, defend the regulatory case.',
    steps: [
      { step: 'Real-time online DO + T + ORP at 2 depths, telemetered to WTW',
        target: 'DO at sediment interface alarm < 4 mg/L',
        evidence: 'Continuous-monitoring standard at all benchmark sites',
        module: 'davison_fe_mn',
        kpi: '> 99 % of summer hours with DO_interface > 4 mg/L' },
      { step: 'Quarterly chl-a + cyano fraction laboratory analysis',
        target: 'Cyano fraction < 25 %, total chl-a < 30 µg/L summer mean',
        evidence: 'Rutland Madgwick 2006; Bautzen Hülsmann 2005',
        module: 'csr_groups',
        kpi: '−70 % vs pre-scheme cyanobacterial biomass' },
      { step: 'Diffuser fouling drift inspection annually (April)',
        target: 'Replace > 10 % flux-drop diffusers; re-tension air-lines',
        evidence: 'Petzoldt 2010 (Bautzen 10-yr maintenance log)',
        pitfall: 'Skip year 5 → biofilm fouling drops OTE ~ 1 % per year' },
      { step: 'Five-year regulatory submission to EA & DWI',
        target: 'Compliance dashboard: Mn < 50 µg/L, geosmin < 10 ng/L, internal P < baseline × 0.4',
        evidence: 'UK DWI Regulation 27 / Drinking-Water Inspectorate guidance',
        module: 'geosmin_predictor' },
      { step: 'Re-run module-train summary annually with updated inputs',
        target: 'Verify predicted vs observed delta < 25 % across all 6 stages',
        evidence: 'Continuous-improvement standard',
        module: 'cost_per_o2',
        kpi: '£/kg O₂ trend stable or decreasing' },
    ],
  },
];

interface KPI {
  metric: string;
  target: string;
  evidence: string;
  module: string;
}

const KPIS: KPI[] = [
  { metric: 'Observed OTE',           target: '> 25 %',               evidence: 'Rutland 26 %, Bautzen 40 %',          module: 'mcginnis_bubble' },
  { metric: 'Plume coverage',         target: '> 80 % of basin',      evidence: 'Hex-grid rule (Asaeda-Imberger)',     module: 'asaeda_c' },
  { metric: 'DO at sediment interface', target: '> 4 mg/L sustained', evidence: 'Sempach, Hallwil, Bautzen',          module: 'davison_fe_mn' },
  { metric: 'Stratification breakdown time', target: '≤ 10 days',     evidence: 'Bautzen 6 d, Rutland < 14 d',         module: 'asaeda_c' },
  { metric: 'Sediment ORP',           target: '> 250 mV',             evidence: 'Davison 1993 review',                  module: 'redox_p_release' },
  { metric: 'Internal P loading',     target: '−60 % vs baseline',    evidence: 'Amisk 60 %, Bautzen 85 %, Hallwil 70 %', module: 'redox_p_release' },
  { metric: 'Mn in raw water',        target: '< 50 µg/L (DWI PCV)',  evidence: 'Hanningfield 0.4 → 0.05 mg/L',         module: 'davison_fe_mn' },
  { metric: 'Geosmin / 2-MIB',        target: '< 10 ng/L (DWI)',      evidence: 'Rutland 30 → 8 ng/L after retrofit',   module: 'geosmin_predictor' },
  { metric: 'Cyano fraction',         target: '< 25 % biovolume',     evidence: 'Rutland −70 %, Bautzen −60 %',         module: 'csr_groups' },
  { metric: '£ per kg O₂ delivered',  target: '< £1.5 / kg',          evidence: 'Stenstrom 2008; Ofwat PR19',           module: 'cost_per_o2' },
  { metric: 'Macrofaunal density',    target: '> 500 ind/m²',         evidence: 'Hallwil Tanytarsus recovery',          module: 'aller_bioturbation' },
  { metric: 'Bed shear at modules',   target: '< 0.08 Pa (< 0.6·τ_ce)', evidence: 'Amisk 1991 bed-scour lesson',         module: 'mehta_partheniades' },
];

export default function PerfectScenarioPage() {
  const navigate = useNavigate();
  const { isEnabled, toggle } = useModulesEnabled();
  const train = getTrain(PERFECT_TRAIN_ID);

  const activate = () => {
    if (!train) return;
    // 1. Enable every module in Train A
    for (const id of train.moduleIds) {
      if (!isEnabled(id)) toggle(id);
    }
    // 2. Push Barcombe inputs as the active scenario
    try {
      sessionStorage.setItem(TRAIN_SCENARIO_KEY, JSON.stringify({
        siteId: 'barcombe_perfect',
        siteName: 'Barcombe (perfect scenario)',
        inputs: BARCOMBE_INPUTS,
        timestamp: Date.now(),
      }));
    } catch (e) {
      console.warn('Could not persist scenario:', e);
    }
    // 3. Open the train summary with bathymetry
    navigate(`/train-summary/${PERFECT_TRAIN_ID}`);
  };

  const enabledInTrain = train ? train.moduleIds.filter(id => isEnabled(id)).length : 0;
  const allOn = train ? enabledInTrain === train.moduleIds.length : false;

  return (
    <div className="space-y-4 p-4">
      <PageHeader icon={Sparkles} title="Perfect Scenario — Applied to Barcombe"
        subtitle="The 6-phase plan synthesising every proven aeration best-practice into a single, conflict-free implementation route"
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={activate}
              className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 px-3 py-1.5 text-sm font-semibold text-white">
              <Zap className="w-4 h-4" /> Activate scenario
            </button>
            <button onClick={() => navigate(-1)}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-700">
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
          </div>
        }
      />

      {/* Activation status banner */}
      <div className={`rounded-xl border-2 p-3 flex items-center justify-between gap-3 ${allOn ? 'border-emerald-700 bg-emerald-900/15' : 'border-sky-700 bg-sky-900/15'}`}>
        <div className="text-sm flex items-center gap-2">
          {allOn ? <CheckCircle2 className="w-4 h-4 text-emerald-300" /> : <Sparkles className="w-4 h-4 text-sky-300" />}
          <span className={allOn ? 'text-emerald-100' : 'text-sky-100'}>
            {allOn
              ? 'Perfect-scenario train fully active — all 12 modules enabled.'
              : `${enabledInTrain}/${train?.moduleIds.length ?? 0} modules currently enabled. Click "Activate scenario" to enable the rest, load Barcombe defaults, and open the Train Summary dashboard with bathymetry.`}
          </span>
        </div>
        <button onClick={activate}
          className="rounded-lg bg-sky-600 hover:bg-sky-500 text-white text-xs font-semibold px-3 py-1.5 inline-flex items-center gap-1">
          <Zap className="w-3.5 h-3.5" /> Activate
        </button>
      </div>

      {/* Reservoir profile summary */}
      <section className="rounded-2xl border border-slate-700 bg-slate-900 p-4">
        <div className="text-base font-bold text-slate-100 mb-2 flex items-center gap-2">
          <Target className="w-4 h-4 text-fuchsia-300" />
          Target reservoir profile: Barcombe (and equivalents)
        </div>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2 text-xs">
          <Stat k="Area"           v="3.4 ha (34 000 m²)" />
          <Stat k="Mean depth"     v="4.5 m" />
          <Stat k="Max depth"      v="8 m" />
          <Stat k="Volume"         v="≈ 153 000 m³" />
          <Stat k="Climate"        v="SE England, lowland" />
          <Stat k="Stratification" v="Weak / polymictic" />
          <Stat k="Summer chl-a"   v="30 µg/L mean, 70 µg/L peak" />
          <Stat k="TP / TN"        v="80 µg/L / 1.2 mg/L" />
          <Stat k="NOM / pH"       v="8 mg/L / 7.6" />
          <Stat k="Bed type"       v="Organic-rich cohesive" />
          <Stat k="Risks"          v="Cyano + T/O + Mn breakthrough" />
          <Stat k="Downstream WTW" v="South East Water — Barcombe Mills WTW" />
        </div>
        <p className="mt-3 text-xs text-slate-400 leading-relaxed">
          This profile maps directly onto Train A (Shallow Eutrophic UK Reservoir). The 12-module bundle and the
          design template below are derived from the documented successes at <strong className="text-slate-200">Rutland</strong>,
          <strong className="text-slate-200"> Bautzen</strong>, <strong className="text-slate-200">Hanningfield</strong> retrofit,
          <strong className="text-slate-200"> Loch Leven</strong>, and <strong className="text-slate-200">Müggelsee</strong>.
        </p>
      </section>

      {/* Current Barcombe health snapshot */}
      <ReservoirHealthScorecard params={BARCOMBE_INPUTS} scenarioName="Barcombe — current baseline" />

      {/* The 6 phases */}
      <section className="space-y-3">
        <div className="text-base font-bold text-slate-100 flex items-center gap-2">
          <ClipboardCheck className="w-4 h-4 text-sky-300" />
          The 6-phase plan
        </div>
        {PHASES.map(phase => (
          <PhaseCard key={phase.num} phase={phase} />
        ))}
      </section>

      {/* KPIs */}
      <section className="rounded-2xl border-2 border-emerald-700/60 bg-emerald-950/15 p-4">
        <div className="text-base font-bold text-slate-100 mb-3 flex items-center gap-2">
          <Gauge className="w-4 h-4 text-emerald-300" />
          Success KPI dashboard — track these after commissioning
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs text-left">
            <thead>
              <tr className="border-b-2 border-emerald-800 text-emerald-300">
                <th className="py-2 pr-3">Metric</th>
                <th className="py-2 pr-3">Target</th>
                <th className="py-2 pr-3">Evidence base</th>
                <th className="py-2 pr-3">Predicted by</th>
              </tr>
            </thead>
            <tbody>
              {KPIS.map((k, i) => (
                <tr key={i} className="border-b border-emerald-900/50">
                  <td className="py-1.5 pr-3 text-slate-100 font-semibold">{k.metric}</td>
                  <td className="py-1.5 pr-3 text-emerald-300 font-mono">{k.target}</td>
                  <td className="py-1.5 pr-3 text-slate-400">{k.evidence}</td>
                  <td className="py-1.5 pr-3">
                    <ModuleInfoModal moduleId={k.module} triggerLabel="module ↗" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Cost / benefit synthesis */}
      <section className="rounded-2xl border border-fuchsia-700/40 bg-fuchsia-950/15 p-4">
        <div className="text-base font-bold text-slate-100 mb-2 flex items-center gap-2">
          <PoundSterling className="w-4 h-4 text-fuchsia-300" />
          Expected cost / benefit envelope (Barcombe scale)
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
          <CostCard title="CAPEX" amount="£ 320 – 480 k"
            detail="14-18 fine-bubble membrane diffusers + air-lines + on-shore VFD blower house. Includes commissioning, civils, controls." />
          <CostCard title="OPEX (Year 1)" amount="£ 18 – 28 k"
            detail="2.0 L/s × 14-18 diffusers × £0.20/kWh × 0.55 kWh/Nm³ × seasonal duty + maintenance + monitoring." />
          <CostCard title="Avoided PAC at WTW" amount="£ 35 – 90 k / yr"
            detail="Documented Rutland savings post-retrofit — geosmin events reduced from > 20 to < 8 ng/L means PAC dosing eliminated most summer weeks." />
        </div>
        <p className="mt-3 text-xs text-slate-400 leading-relaxed">
          Payback typically <strong className="text-fuchsia-200">5-8 years</strong> on PAC-OPEX avoidance alone.
          Mn-WTW treatment OPEX (additional savings not in the table above) extends payback ~ 1-2 yr further.
        </p>
      </section>

      {/* Defensibility link */}
      <div className="rounded-xl border-2 border-emerald-700/60 bg-emerald-950/15 p-4 flex items-center justify-between gap-3 flex-wrap">
        <div className="text-sm text-emerald-100 leading-relaxed">
          <strong>Defensibility:</strong> every numeric claim, threshold and equation in this scenario is traceable to a primary peer-reviewed source or statutory text — see the full Defensibility Statement for evidence chain, assumptions, calibration bounds and conflict disclosures.
        </div>
        <button onClick={() => navigate('/defensibility')}
          className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 px-3 py-1.5 text-sm font-semibold text-white">
          View Defensibility Statement
        </button>
      </div>

      {/* Why this works — references */}
      <section className="rounded-xl border border-slate-700 bg-slate-900 p-4 text-xs text-slate-400 leading-relaxed">
        <div className="text-sm font-semibold text-slate-100 mb-2 flex items-center gap-2">
          <BookOpen className="w-4 h-4 text-rb-brand" /> Why this scenario is "perfect"
        </div>
        <ul className="list-disc ml-5 space-y-1">
          <li><strong className="text-slate-200">Conflict-free</strong> — Train A's 12 modules are explicitly checked for hard / soft conflicts; none remain.</li>
          <li><strong className="text-slate-200">Multi-site proven</strong> — every design choice in Phase 3 is replicated successfully at 2 or more independent reservoirs.</li>
          <li><strong className="text-slate-200">Numeric KPIs</strong> — each phase carries a measurable target (OTE, OK τ_b, DO_interface, etc.) so commissioning has hard pass/fail criteria.</li>
          <li><strong className="text-slate-200">Lifecycle-economic</strong> — Stenstrom 2008 cost model is built into the train; the design is the WLC optimum, not just the cheapest CAPEX.</li>
          <li><strong className="text-slate-200">Regulator-defendable</strong> — every claim is traceable to peer-reviewed primary literature, surfaced through the ModuleInfoModal popups.</li>
        </ul>
        <p className="mt-3 italic text-slate-500">
          Click <em>Activate scenario</em> at the top to load the train, push the Barcombe defaults into the simulator, and open the
          Train Summary dashboard with bathymetric module placement ready to refine.
        </p>
      </section>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────

function PhaseCard({ phase }: { phase: Phase }) {
  return (
    <div className="rounded-2xl border border-slate-700 bg-slate-900 p-4">
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-sky-900/40 border-2 border-sky-700 flex items-center justify-center">
          <phase.icon className="w-5 h-5 text-sky-300" />
        </div>
        <div className="flex-1">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="text-xs uppercase tracking-wider text-sky-300 font-bold">Phase {phase.num}</span>
            <h3 className="text-base font-bold text-slate-100">{phase.title}</h3>
          </div>
          <div className="text-xs text-slate-400 mt-1">{phase.goal}</div>
        </div>
      </div>
      <div className="mt-3 space-y-2">
        {phase.steps.map((s, i) => (
          <div key={i} className="rounded-lg border border-slate-700/70 bg-slate-950/40 p-3">
            <div className="flex items-start gap-2">
              <ChevronRight className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <div className="text-sm text-slate-100 font-semibold">{s.step}</div>
                <div className="mt-1.5 grid grid-cols-1 md:grid-cols-3 gap-2 text-[11px]">
                  <Cell label="Target" colour="emerald" value={s.target} />
                  <Cell label="Evidence" colour="sky" value={s.evidence} />
                  {s.module && (
                    <div className="rounded-md border border-fuchsia-700/40 bg-fuchsia-900/15 p-2">
                      <div className="text-[10px] uppercase tracking-wider text-fuchsia-300 mb-0.5">Module</div>
                      <ModuleInfoModal moduleId={s.module} triggerLabel="module ↗" />
                    </div>
                  )}
                </div>
                {(s.kpi || s.pitfall) && (
                  <div className="mt-2 space-y-1">
                    {s.kpi && (
                      <div className="text-[11px] text-emerald-200 flex items-start gap-1.5">
                        <Gauge className="w-3 h-3 mt-0.5 flex-shrink-0" />
                        <span><strong>KPI:</strong> {s.kpi}</span>
                      </div>
                    )}
                    {s.pitfall && (
                      <div className="text-[11px] text-amber-200 flex items-start gap-1.5">
                        <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                        <span><strong>Pitfall:</strong> {s.pitfall}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Cell({ label, value, colour }: { label: string; value: string; colour: 'emerald' | 'sky' | 'fuchsia' }) {
  const cls = colour === 'emerald' ? 'border-emerald-700/40 bg-emerald-900/15 text-emerald-300'
    : colour === 'sky' ? 'border-sky-700/40 bg-sky-900/15 text-sky-300'
    : 'border-fuchsia-700/40 bg-fuchsia-900/15 text-fuchsia-300';
  return (
    <div className={`rounded-md border p-2 ${cls.split(' ').slice(0, 2).join(' ')}`}>
      <div className={`text-[10px] uppercase tracking-wider mb-0.5 ${cls.split(' ')[2]}`}>{label}</div>
      <div className="text-slate-200">{value}</div>
    </div>
  );
}

function Stat({ k, v }: { k: string; v: string }) {
  return (
    <div className="rounded-md border border-slate-700 bg-slate-950/40 p-2">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{k}</div>
      <div className="text-slate-200">{v}</div>
    </div>
  );
}

function CostCard({ title, amount, detail }: { title: string; amount: string; detail: string }) {
  return (
    <div className="rounded-xl border border-fuchsia-700/40 bg-slate-900 p-3">
      <div className="text-xs uppercase tracking-wider text-fuchsia-300 font-semibold">{title}</div>
      <div className="text-xl font-bold text-slate-100 mt-1 font-mono">{amount}</div>
      <div className="text-[11px] text-slate-400 mt-1 leading-snug">{detail}</div>
    </div>
  );
}
